import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../utils/utils";
import { setUserData } from "../redux/userStore";
import { getOrganizationData } from "./organization";
import { setLoadingContext } from "../redux/loaderStore";
import { PAGE_LOADER } from "../constants/loader";
import { showModal } from "../redux/modalStore";
import { useRef } from "react";

const useFetchOrganizationData = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orgId } = useParams();

  const sessionToken = localStorage.getItem("session_token");
  const organizations = useSelector((state) => state.user.organizations);
  const hasOrganizations = Object.keys(organizations || {}).length > 0;
  const currentOrganizationId = useSelector(
    (state) => state.user.organizations[orgId]?.UUID,
  );
  const lastFetchedOrgIdRef = useRef(null);

  useEffect(() => {
    if (!orgId || !hasOrganizations) return;

    if (lastFetchedOrgIdRef.current === orgId) return;

    // If data already matches the URL org, mark as fetched and skip.
    if (currentOrganizationId === orgId) {
      lastFetchedOrgIdRef.current = orgId;
      return;
    }

    lastFetchedOrgIdRef.current = orgId;

    // Organization data needs to be refreshed
    dispatch(setLoadingContext({ context: PAGE_LOADER, isLoading: true }));

    dispatch(getOrganizationData(orgId, sessionToken, navigate, false))
      .then(() => {
        const refreshQuery = "&refresh=true";
        return axiosInstance
          .request({
            method: "GET",
            url: `/auth/session?session_token=${sessionToken}${refreshQuery}`,
          })
          .then((response) => {
            dispatch(setUserData(response.data));
            localStorage.setItem("last_refresh_timestamp", Date.now());
          })
          .catch(() => {
            // Allow future retries if refresh fails
            lastFetchedOrgIdRef.current = null;
            dispatch(showModal({ name: "error_modal" }));
          });
      })
      .catch(() => {
        lastFetchedOrgIdRef.current = null;
      })
      .finally(() => {
        dispatch(setLoadingContext({ context: PAGE_LOADER, isLoading: false }));
      });
  }, [
    orgId,
    currentOrganizationId,
    dispatch,
    sessionToken,
    navigate,
    hasOrganizations,
  ]);
};

export default useFetchOrganizationData;
